import React, { useRef, useEffect } from "react";
import HeroSectionReadingPlan from "@/components/HeroSectionReadingPlan";
import ReadingPlanForm from "@/components/ReadingPlanForm";
import ReadingPlanResult from "@/components/ReadingPlanResult";
import LoadingScriptures from "@/components/LoadingScriptures";
import useReadingPlan from "@/hooks/useReadingPlan";
import { useSearchParams } from "react-router-dom";
import { useScrollToTop } from "@/hooks/useScrollToTop";
const ReadingPlan: React.FC = () => {
  // Use the scroll to top hook to ensure page starts at top every time
  useScrollToTop();
  const {
    topic,
    setTopic,
    days,
    setDays,
    isLoading,
    isSaving,
    readingPlan,
    handleSubmit,
    saveReadingPlan
  } = useReadingPlan();
  const [searchParams] = useSearchParams();
  const resultRef = useRef<HTMLDivElement>(null);

  // Scroll to results when they load
  useEffect(() => {
    if (readingPlan && !isLoading && resultRef.current) {
      setTimeout(() => {
        resultRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 300);
    }
  }, [readingPlan, isLoading]);
  useEffect(() => {
    const topicParam = searchParams.get('topic');
    const daysParam = searchParams.get('days');
    if (topicParam) {
      setTopic(topicParam);
    }
    if (daysParam && !isNaN(parseInt(daysParam))) {
      setDays(parseInt(daysParam));
    }
  }, [searchParams, setTopic, setDays]);
  return <div className="min-h-screen bg-[#f8f8f8]">
      {/* Hero Section */}
      <div className="pt-16 sm:pt-24 pb-8 sm:pb-16 text-center px-4">
        <h1 className="text-4xl sm:text-7xl font-playfair mb-4 sm:mb-6 py-[39px]">
          <span className="text-black">Bible </span>
          <span className="text-heaven-gold">Reading Plan</span>
        </h1>
        <p className="text-gray-600 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
          Generate personalized 7-10 day Scripture reading plans based on any topic, with 
          devotional commentary, actionable steps, and prayer prompts for each day.
        </p>
      </div>

      {/* Search Form Container */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <div className="bg-white rounded-2xl shadow-md p-4 sm:p-8">
          <h2 className="text-2xl sm:text-3xl text-[#4169E1] text-center mb-2 sm:mb-3">
            What Topics Would You Like to Explore?
          </h2>
          <p className="text-gray-600 text-center text-sm sm:text-base mb-4 sm:mb-6">
            Create a customized reading plan with daily devotional insights and prayer points.
          </p>
          
          <ReadingPlanForm topic={topic} days={days} isLoading={isLoading} onTopicChange={setTopic} onDaysChange={setDays} onSubmit={handleSubmit} />
        </div>

        {/* Results Section */}
        {isLoading ? <LoadingScriptures /> : readingPlan && <div ref={resultRef} className="mt-6 sm:mt-8">
              <div className="mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-playfair font-bold text-gray-800 mb-2">
                  Reading Plan for "{topic}"
                </h2>
                <p className="text-gray-600 text-sm sm:text-base">
                  Your {days}-day reading plan is ready.
                </p>
              </div>
              
              <ReadingPlanResult readingPlan={readingPlan} topic={topic} onSave={saveReadingPlan} isSaving={isSaving} />
            </div>}
      </div>
    </div>;
};
export default ReadingPlan;